import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { COLORS, BRAND } from '@/lib/brand';
import { LogOut, LayoutDashboard, Package, Truck, ClipboardCheck } from 'lucide-react';
import api from '@/lib/api';

const BodegaLayout = ({ children }) => {
  const { user, logout, warehouseId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [warehouseName, setWarehouseName] = useState('Bodega');

  useEffect(() => {
    const fetchWarehouse = async () => {
      if (warehouseId) {
        try {
          const res = await api.get('/warehouses/active');
          const warehouse = res.data.find(w => w.id === warehouseId);
          if (warehouse) {
            setWarehouseName(warehouse.name);
          }
        } catch (error) {
          console.error('Error fetching warehouse:', error);
        }
      }
    };
    fetchWarehouse();
  }, [warehouseId]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/bodega', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/bodega/inventory', label: 'Inventario', icon: Package },
    { path: '/bodega/recepcion', label: 'Recepción', icon: Truck },
    { path: '/bodega/recuentos', label: 'Recuentos', icon: ClipboardCheck },
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: COLORS.background.DEFAULT }}>
      {/* Header con fondo naranja TUPU Stock */}
      <header 
        className="px-6 py-3 sticky top-0 z-40 shadow-md"
        style={{ backgroundColor: COLORS.primary.DEFAULT }}
        data-testid="bodega-header"
      >
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <div 
              className="h-10 w-10 rounded-lg flex items-center justify-center shadow-sm"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <Package className="w-6 h-6" style={{ color: COLORS.primary.DEFAULT }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{warehouseName}</h1>
              <p className="text-sm text-white/80">{user?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = item.exact 
                  ? location.pathname === item.path
                  : location.pathname.startsWith(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                    style={{
                      backgroundColor: isActive ? '#FFFFFF' : 'transparent',
                      color: isActive ? COLORS.primary.DEFAULT : '#FFFFFF'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = COLORS.primary.hover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    data-testid={`bodega-nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-white/90 hover:text-white"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              data-testid="bodega-logout-button"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium hidden sm:inline">Cerrar sesión</span>
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav 
          className="md:hidden grid grid-cols-4 gap-1.5 mt-3 pt-3"
          style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact 
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-lg transition-all"
                data-testid={`bodega-mobile-nav-${item.label.toLowerCase()}`}
                style={{
                  backgroundColor: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.1)',
                  color: isActive ? COLORS.primary.DEFAULT : '#FFFFFF'
                }}
              >
                <Icon className="w-4 h-4" />
                <span className="text-[11px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </header>

      <main 
        className="flex-1 px-4 py-4 md:p-6 max-w-7xl mx-auto w-full" 
        data-testid="bodega-main-content"
      >
        {children}
      </main>

      {/* Footer */}
      <footer 
        className="hidden md:block px-6 py-4" 
        style={{ backgroundColor: COLORS.background.surface, borderTop: `1px solid ${COLORS.border.DEFAULT}` }}
      >
        <div className="max-w-7xl mx-auto text-center text-sm" style={{ color: COLORS.text.secondary }}>
          {BRAND.name} - {warehouseName}
        </div>
      </footer>
    </div>
  );
};

export default BodegaLayout;
